import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  // Sticky behavior with backdrop blur
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`header-professional ${isScrolled ? 'scrolled' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(10, 10, 10, 0.8)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid var(--stroke-hairline)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div className="container-professional">
        <div className="flex justify-between items-center" style={{ height: '64px' }}>
          
          <Link 
            to="/" 
            className="flex items-center py-2 px-2 rounded-md transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background hover:opacity-80"
            aria-label="AffinityAim - Home"
          >
            <img 
              src="/lovable-uploads/564b590b-a081-470a-ae9e-f75de8d420db.png" 
              alt="AffinityAim logo" 
              className="logo-professional"
              width="560"
              height="200"
              loading="eager"
              decoding="async"
              style={{
                height: '64px',
                width: 'auto',
                imageRendering: 'crisp-edges',
                transition: 'all 0.3s ease',
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <nav className="flex items-center gap-6" role="navigation" aria-label="Main navigation">
              <Link 
                to="/services" 
                className={`text-sm font-medium transition-all duration-300 ${
                  isActive('/services') 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.7)] hover:text-white'
                }`}
              >
                Services
              </Link>
              <Link 
                to="/portfolio" 
                className={`text-sm font-medium transition-all duration-300 ${
                  isActive('/portfolio') 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.7)] hover:text-white'
                }`}
              >
                Portfolio
              </Link>
              <Link 
                to="/about" 
                className={`text-sm font-medium transition-all duration-300 ${
                  isActive('/about') 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.7)] hover:text-white'
                }`}
              >
                About
              </Link>
              <Link 
                to="/pricing" 
                className={`text-sm font-medium transition-all duration-300 ${
                  isActive('/pricing') 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.7)] hover:text-white'
                }`}
              >
                Pricing
              </Link>
            </nav>
            <Link to="/contact">
              <button 
                className="h-10 px-6 text-sm bg-[#00e5ff] hover:bg-[#00d4e6] text-white font-semibold rounded-lg transition-all duration-200 hover:transform hover:-translate-y-0.5"
              >
                Start Project
              </button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            style={{ color: 'hsl(var(--muted-foreground))' }}
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            id="mobile-menu" 
            className="md:hidden py-6 border-t"
            style={{ borderColor: 'var(--stroke-hairline)' }}
          >
            <nav className="flex flex-col space-y-4" role="navigation" aria-label="Mobile navigation">
              <Link 
                to="/services" 
                className="text-muted-foreground hover:text-foreground transition-colors py-2 text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                Services
              </Link>
              <Link 
                to="/portfolio" 
                className="text-muted-foreground hover:text-foreground transition-colors py-2 text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                Portfolio
              </Link>
              <Link 
                to="/about" 
                className="text-muted-foreground hover:text-foreground transition-colors py-2 text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/pricing" 
                className="text-muted-foreground hover:text-foreground transition-colors py-2 text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
              <Link to="/contact" onClick={() => setIsOpen(false)} className="pt-2">
                <button 
                  style={{
                    background: 'var(--gradient-primary)',
                    color: 'white',
                    fontWeight: 600,
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    width: '100%',
                    fontSize: '16px',
                  }}
                >
                  Start Project
                </button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
